// src/hooks/useFileUpload.ts

import { useState, useCallback, useRef, useEffect } from "react";
import { UploadedFile } from "../components/chat/FileUploadCarousel";
import {
  validateFiles,
  createFileId,
  FileValidationResult,
  FileValidationConfig,
} from "../utils/fileUtils";
import { createLogger } from "../utils/logger";
import { EnhancedFile } from "../interfaces/sharePointTypes";
import { track } from "../utils/analytics";
import { AttachmentSource } from "../interfaces/chatSession";

const log = createLogger("useFileUpload");

/**
 * Gets a unique identifier for a file
 * For SharePoint files: uses sharePointFileId
 * For regular files: uses name-size signature
 */
function getFileIdentifier(file: File): string {
  // Check for SharePoint metadata
  const enhancedFile = file as EnhancedFile;
  const sharePointFileId = enhancedFile.sharePointFileId;

  if (sharePointFileId) {
    return `sharepoint:${sharePointFileId}`;
  }

  // Fallback to file signature (name + size)
  // Note: We exclude lastModified because SharePoint files get new timestamps on download
  return `file:${file.name}-${file.size}`;
}

/**
 * Deduplicates files by checking SharePoint ID or file signature
 * @param newFiles - Files to check for duplicates
 * @param existingFiles - Currently attached files
 * @returns Object with unique files and duplicate count
 */
function deduplicateFiles(
  newFiles: File[],
  existingFiles: UploadedFile[]
): { uniqueFiles: File[]; duplicateCount: number } {
  // Create a Set of existing file identifiers for fast lookup
  const existingIdentifiers = new Set<string>();

  existingFiles.forEach((uploadedFile) => {
    const file = uploadedFile.file;
    const identifier = getFileIdentifier(file);
    existingIdentifiers.add(identifier);
  });

  // Filter out duplicates
  const uniqueFiles: File[] = [];
  let duplicateCount = 0;

  newFiles.forEach((file) => {
    const identifier = getFileIdentifier(file);

    if (existingIdentifiers.has(identifier)) {
      duplicateCount++;
      log.debug("Duplicate file detected", {
        fileName: file.name,
        identifier,
        isSharePointFile: !!(file as EnhancedFile).sharePointFileId,
      });
    } else {
      uniqueFiles.push(file);
      // Add to set to catch duplicates within the same batch
      existingIdentifiers.add(identifier);
    }
  });

  return { uniqueFiles, duplicateCount };
}

/**
 * File upload state and callbacks
 */
export interface UseFileUploadReturn {
  // State
  files: UploadedFile[];
  error: string | null;
  isUploading: boolean;

  // Actions
  /**
   * @param source Which affordance the user reached for. Both paths land
   *   here, so it has to be passed in to tell them apart in reporting.
   */
  addFiles: (newFiles: File[], source?: AttachmentSource) => Promise<void>;
  removeFile: (fileId: string) => void;
  clearFiles: () => void;
  clearError: () => void;

  // Utilities
  canAddMoreFiles: boolean;
  remainingFileSlots: number;
}

/**
 * Custom hook for managing file uploads
 *
 * This hook provides a complete file upload management system with:
 * - File validation (type, size, count)
 * - Upload state tracking
 * - Error handling
 * - File removal and clearing
 *
 * @param onFilesChange - Optional callback when files change
 * @param validationConfig - Optional agent-specific validation configuration
 * @returns File upload state and control functions
 */
export function useFileUpload(
  onFilesChange?: (files: File[]) => void,
  validationConfig?: FileValidationConfig
): UseFileUploadReturn {
  // State for uploaded files
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Track upload operations to handle cleanup
  const uploadAbortControllers = useRef<Map<string, AbortController>>(new Map());

  // Track error timeout for auto-dismiss
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate remaining slots based on agent configuration
  const maxFileCount = validationConfig?.maxFileCount || 5; // Default fallback
  const remainingFileSlots = maxFileCount - files.length;
  const canAddMoreFiles = remainingFileSlots > 0;

  // Clean up error timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Simulate file upload process
   * In a real implementation, this would upload to a server
   * For now, we just simulate the process with a delay
   */
  const simulateFileUpload = async (file: File, fileId: string): Promise<void> => {
    // Create an abort controller for this upload
    const abortController = new AbortController();
    uploadAbortControllers.current.set(fileId, abortController);

    try {
      // Simulate upload delay (500ms - 2s based on file size)
      const uploadDuration = Math.min(500 + file.size / 1024, 2000);

      await new Promise((resolve, reject) => {
        const timeout = setTimeout(resolve, uploadDuration);

        // Handle abort
        abortController.signal.addEventListener("abort", () => {
          clearTimeout(timeout);
          reject(new Error("Upload cancelled"));
        });
      });

      // Clean up abort controller
      uploadAbortControllers.current.delete(fileId);
    } catch (error) {
      // Clean up on error
      uploadAbortControllers.current.delete(fileId);
      throw error;
    }
  };

  /**
   * Add new files to the upload list
   * Validates files and manages upload state
   */
  const addFiles = useCallback(
    async (newFiles: File[], source: AttachmentSource = "unknown") => {
      // Clear any existing error
      setError(null);

      // Deduplicate files before validation
      const { uniqueFiles, duplicateCount } = deduplicateFiles(newFiles, files);

      // Log duplicates silently (no user-facing error)
      if (duplicateCount > 0) {
        log.info("Duplicate files skipped (silent)", {
          duplicateCount,
          attemptedCount: newFiles.length,
          uniqueCount: uniqueFiles.length,
        });
      }

      // If all files were duplicates, exit early
      if (uniqueFiles.length === 0) {
        log.info("All files were duplicates, no files added");
        return;
      }

      // Validate all files with agent-specific configuration
      const validationResults = validateFiles(uniqueFiles, files.length, validationConfig);

      // Check if any validation failed
      const failedValidation = validationResults.find((result) => !result.isValid);
      if (failedValidation) {
        setError(failedValidation.error || "File validation failed");

        // Clear any existing error timeout
        if (errorTimeoutRef.current) {
          clearTimeout(errorTimeoutRef.current);
        }

        // Auto-dismiss error after 7 seconds
        errorTimeoutRef.current = setTimeout(() => {
          setError(null);
          errorTimeoutRef.current = null;
          log.debug("Auto-dismissed file upload error after timeout");
        }, 7000);

        log.warn("File validation failed", {
          error: failedValidation.error,
          errorCode: failedValidation.errorCode,
          fileCount: newFiles.length,
          validationConfig,
        });
        return;
      }

      // Attached, and past validation - so this counts. Reported here rather
      // than at the two call sites because both funnel through this function,
      // and only here is it known which files actually survived dedup and
      // validation. Rejected and duplicate files are deliberately not counted.
      track("attachment:added", {
        source,
        file_count: uniqueFiles.length,
        file_types: uniqueFiles.map((file) =>
          file.name.includes(".")
            ? (file.name.split(".").pop() || "unknown").toLowerCase()
            : "unknown"
        ),
        total_bytes: uniqueFiles.reduce((sum, file) => sum + file.size, 0),
        duplicates_skipped: duplicateCount,
      });

      // Create UploadedFile objects with uploading state
      // Preserve SharePoint metadata if present
      const uploadedFiles: UploadedFile[] = uniqueFiles.map((file) => {
        // Check if file is already downloaded (from SharePoint placeholder replacement)
        const enhancedFile = file as EnhancedFile;
        const isAlreadyDownloaded = enhancedFile.isAlreadyDownloaded || false;

        return {
          file,
          id: createFileId(file),
          isUploading: !isAlreadyDownloaded, // Skip upload state if already downloaded
          uploadProgress: isAlreadyDownloaded ? 100 : 0,
          // Extract SharePoint metadata from File object (added by downloadFileAsFile)
          isSharePointFile: enhancedFile.isSharePointFile || false,
          sharePointWebUrl: enhancedFile.sharePointWebUrl || null,
        };
      });

      // Add files to state immediately (showing loading state)
      setFiles((prev) => [...prev, ...uploadedFiles]);
      setIsUploading(true);

      log.info("Starting file uploads", {
        fileCount: uniqueFiles.length,
        fileNames: uniqueFiles.map((f) => f.name),
        maxFileCount,
        currentFileCount: files.length,
      });

      // Simulate upload for each file (skip if already downloaded)
      const uploadPromises = uploadedFiles.map(async (uploadedFile) => {
        // Skip simulation if file is already downloaded (from SharePoint)
        if (!uploadedFile.isUploading) {
          log.info("File already downloaded, skipping upload simulation", {
            fileName: uploadedFile.file.name,
            fileId: uploadedFile.id,
          });
          return;
        }

        try {
          await simulateFileUpload(uploadedFile.file, uploadedFile.id);

          // Update file to mark upload complete
          setFiles((prev) =>
            prev.map((f) =>
              f.id === uploadedFile.id ? { ...f, isUploading: false, uploadProgress: 100 } : f
            )
          );

          log.info("File upload completed", {
            fileName: uploadedFile.file.name,
            fileId: uploadedFile.id,
          });
        } catch (error) {
          // Handle upload error
          if (error instanceof Error && error.message === "Upload cancelled") {
            // Remove cancelled file
            setFiles((prev) => prev.filter((f) => f.id !== uploadedFile.id));
            log.info("File upload cancelled", {
              fileName: uploadedFile.file.name,
            });
          } else {
            // Mark file as failed
            setFiles((prev) =>
              prev.map((f) =>
                f.id === uploadedFile.id ? { ...f, isUploading: false, error: "Upload failed" } : f
              )
            );
            log.error("File upload failed", {
              fileName: uploadedFile.file.name,
              error: error instanceof Error ? error.message : String(error),
            });
          }
        }
      });

      // Wait for all uploads to complete
      await Promise.all(uploadPromises);
      setIsUploading(false);

      // Notify parent component of file changes
      if (onFilesChange) {
        const currentFiles = files.map((f) => f.file).concat(uniqueFiles);
        onFilesChange(currentFiles);
      }
    },
    [files, onFilesChange, validationConfig, maxFileCount]
  );

  /**
   * Remove a file from the list
   */
  const removeFile = useCallback(
    (fileId: string) => {
      // Cancel upload if in progress
      const abortController = uploadAbortControllers.current.get(fileId);
      if (abortController) {
        abortController.abort();
        uploadAbortControllers.current.delete(fileId);
      }

      // Remove from state
      setFiles((prev) => {
        const newFiles = prev.filter((f) => f.id !== fileId);

        // Notify parent component
        if (onFilesChange) {
          onFilesChange(newFiles.map((f) => f.file));
        }

        log.info("File removed", {
          fileId,
          remainingCount: newFiles.length,
        });

        return newFiles;
      });

      // Clear error if no files left
      setFiles((prev) => {
        if (prev.length === 0) {
          setError(null);
        }
        return prev;
      });
    },
    [onFilesChange]
  );

  /**
   * Clear all files
   */
  const clearFiles = useCallback(() => {
    // Cancel all ongoing uploads
    uploadAbortControllers.current.forEach((controller) => controller.abort());
    uploadAbortControllers.current.clear();

    // Clear any existing error timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }

    // Clear state
    setFiles([]);
    setError(null);
    setIsUploading(false);

    // Notify parent component
    if (onFilesChange) {
      onFilesChange([]);
    }

    log.info("All files cleared");
  }, [onFilesChange]);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    // Clear any existing error timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    setError(null);
  }, []);

  return {
    // State
    files,
    error,
    isUploading,

    // Actions
    addFiles,
    removeFile,
    clearFiles,
    clearError,

    // Utilities
    canAddMoreFiles,
    remainingFileSlots,
  };
}
